import React, { useState } from 'react';
import { Patient, LabResult } from '../types/patient';
import { Heart, Activity, TrendingUp, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getBadgeClasses, getRiskIcon } from '../utils/styleHelpers';
import { CLEVELAND_HEARTLAB_RANGES, getReferenceRangeDisplay, getTargetValue } from '../config/referenceRanges';
import { subMonths } from 'date-fns';

interface HealthMetricsOverviewProps {
  patient: Patient;
}

export default function HealthMetricsOverview({ patient }: HealthMetricsOverviewProps) {
  const { healthMetrics, labResults } = patient;
  const [timeRange, setTimeRange] = useState<'6m' | '1y' | 'all'>('6m');

  // Helper function to get months from time range
  const getMonthsFromRange = (range: '6m' | '1y' | 'all'): number | null => {
    if (range === '6m') return 6;
    if (range === '1y') return 12;
    return null; // all time - no filtering
  };

  // Helper function to get time range label
  const getTimeRangeLabel = (range: '6m' | '1y' | 'all'): string => {
    if (range === '6m') return '6 months';
    if (range === '1y') return '1 year';
    return 'all time';
  };

  // Helper function to filter data by time range
  const filterDataByTimeRange = (data: any[], range: '6m' | '1y' | 'all') => {
    if (range === 'all') return data;

    const months = getMonthsFromRange(range);
    if (months === null) return data;

    const cutoffDate = subMonths(new Date(), months);

    return data.filter(item => {
      const itemDate = new Date(item.date || item.dateStr);
      return itemDate >= cutoffDate;
    });
  };

  // Helper function to extract historical data from patient.historicalData
  const getHistoricalDataFromPatient = (metricKey: keyof NonNullable<Patient['historicalData']>) => {
    if (!patient.historicalData?.[metricKey]) return [];

    return patient.historicalData[metricKey]!.map((item: any) => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      fullDate: new Date(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      value: item.value,
      dateStr: item.date
    }));
  };

  // Helper function to extract historical data for a specific metric from lab results
  const getHistoricalData = (metricName: string, range: '6m' | '1y' | 'all' = '6m') => {
    const months = getMonthsFromRange(range);

    const relevantLabs = labResults
      .filter(lab => lab.name === metricName)
      .map(lab => ({
        date: new Date(lab.date),
        value: lab.value,
        dateStr: lab.date
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // If 'all' time, return all data without filtering
    if (months === null) {
      return relevantLabs.map(lab => ({
        date: lab.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        fullDate: lab.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        value: lab.value
      }));
    }

    // Otherwise filter by time range
    const cutoffDate = subMonths(new Date(), months);

    return relevantLabs
      .filter(lab => lab.date >= cutoffDate)
      .map(lab => ({
        date: lab.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        fullDate: lab.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        value: lab.value
      }));
  };

  // Helper function to generate mock trend data with realistic variation
  // ONLY for Michael Smith (patient.id === '1') - for all other patients, return empty array
  const generateMockTrendData = (
    currentValue: number,
    trendDirection: 'up' | 'down' | 'stable',
    range: '6m' | '1y' | 'all' = '6m'
  ) => {
    // Only generate mock data for Michael Smith
    if (patient.id !== '1') {
      return [];
    }

    const months = range === '6m' ? 6 : range === '1y' ? 12 : 24; // Use 24 for "all time"
    const variance = currentValue * 0.15;
    const now = new Date();
    const data = [];

    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setMonth(now.getMonth() - i);

      let value;
      // Add ±20% random jitter for realistic clinical variation
      const jitter = (Math.random() - 0.5) * variance * 0.4;

      if (trendDirection === 'up') {
        const trend = currentValue - (variance * (i / months));
        value = trend + jitter; // Add jitter to linear trend
      } else if (trendDirection === 'down') {
        const trend = currentValue + (variance * (i / months));
        value = trend + jitter; // Add jitter to linear trend
      } else { // stable
        // Fluctuate around current value with ±20% variation (40% total variance)
        value = currentValue + (Math.random() - 0.5) * variance * 0.8;
      }

      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        fullDate: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        value: Math.max(0, value) // Prevent negative values
      });
    }

    return data;
  };

  const getTrendDirection = (risk: string): 'up' | 'down' | 'stable' => {
    if (risk === 'high') return 'up';
    if (risk === 'low') return 'down';
    return 'stable';
  };

  // Helper function to check if a metric exists in lab results
  const hasLabData = (metricName: string | string[]): boolean => {
    const names = Array.isArray(metricName) ? metricName : [metricName];
    return labResults.some(lab => names.some(name => lab.name === name || lab.name.includes(name)));
  };

  // Determine if we have real historical data - check parser data first
  const bmiHistoricalFromParser = getHistoricalDataFromPatient('bmi');
  const bmiHistoricalFiltered = bmiHistoricalFromParser.length > 0
    ? filterDataByTimeRange(bmiHistoricalFromParser, timeRange)
    : getHistoricalData('BMI', timeRange);
  const bmiTrend = bmiHistoricalFiltered.length > 0
    ? bmiHistoricalFiltered
    : generateMockTrendData(healthMetrics.obesityAnalysis.bmi, getTrendDirection(healthMetrics.obesityAnalysis.risk), timeRange);
  const bmiIsMock = bmiHistoricalFiltered.length === 0 && patient.id === '1';

  const glucoseHistoricalFromParser = getHistoricalDataFromPatient('glucose');
  const glucoseHistoricalFiltered = glucoseHistoricalFromParser.length > 0
    ? filterDataByTimeRange(glucoseHistoricalFromParser, timeRange)
    : getHistoricalData('Glucose', timeRange);
  const glucoseTrend = glucoseHistoricalFiltered.length > 0
    ? glucoseHistoricalFiltered
    : generateMockTrendData(healthMetrics.metabolicAnalysis.glucose || 90, getTrendDirection(healthMetrics.metabolicAnalysis.risk), timeRange);
  const glucoseIsMock = glucoseHistoricalFiltered.length === 0 && patient.id === '1';

  const ldlHistoricalFromParser = getHistoricalDataFromPatient('ldlCholesterol');
  const ldlHistoricalFiltered = ldlHistoricalFromParser.length > 0
    ? filterDataByTimeRange(ldlHistoricalFromParser, timeRange)
    : getHistoricalData('LDL Cholesterol', timeRange);
  const ldlTrend = ldlHistoricalFiltered.length > 0
    ? ldlHistoricalFiltered
    : generateMockTrendData(healthMetrics.cholesterolAnalysis.ldl, getTrendDirection(healthMetrics.cholesterolAnalysis.risk), timeRange);
  const ldlIsMock = ldlHistoricalFiltered.length === 0 && patient.id === '1';

  // ApoB trend - check parser data first
  const apoBHistoricalFromParser = getHistoricalDataFromPatient('apoB');
  const apoBHistoricalFiltered = apoBHistoricalFromParser.length > 0
    ? filterDataByTimeRange(apoBHistoricalFromParser, timeRange)
    : getHistoricalData('Apolipoprotein B', timeRange);
  const apoBTrend = healthMetrics.cholesterolAnalysis.apoB
    ? (apoBHistoricalFiltered.length > 0
      ? apoBHistoricalFiltered
      : generateMockTrendData(healthMetrics.cholesterolAnalysis.apoB, getTrendDirection(healthMetrics.cholesterolAnalysis.risk), timeRange))
    : [];
  const apoBIsMock = apoBHistoricalFiltered.length === 0 && healthMetrics.cholesterolAnalysis.apoB !== undefined && patient.id === '1';

  // Blood pressure - check historical data first
  let bloodPressureData;
  if (patient.historicalData?.bloodPressure && patient.historicalData.bloodPressure.length > 0) {
    const bpData = patient.historicalData.bloodPressure.map(bp => ({
      date: new Date(bp.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      fullDate: new Date(bp.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      systolic: bp.systolic || 120,
      diastolic: bp.diastolic || 80,
      dateStr: bp.date
    }));
    bloodPressureData = filterDataByTimeRange(bpData, timeRange);
  } else {
    // Only generate mock data for Michael Smith
    const systolicTrend = generateMockTrendData(healthMetrics.cardiovascularAnalysis.systolic, getTrendDirection(healthMetrics.cardiovascularAnalysis.risk), timeRange);
    const diastolicTrend = generateMockTrendData(healthMetrics.cardiovascularAnalysis.diastolic, getTrendDirection(healthMetrics.cardiovascularAnalysis.risk), timeRange);
    bloodPressureData = systolicTrend.map((item, index) => ({
      date: item.date,
      fullDate: item.fullDate,
      systolic: item.value,
      diastolic: diastolicTrend[index]?.value || 80
    }));
  }

  // Cholesterol trend data combining LDL and ApoB
  const cholesterolTrendData = ldlTrend.map((item, index) => ({
    date: item.date,
    fullDate: item.fullDate,
    ldl: item.value,
    apoB: apoBTrend[index]?.value
  }));

  // Heart rate trend - check historical data first
  let heartRateData;
  if (patient.historicalData?.heartRate && patient.historicalData.heartRate.length > 0) {
    const hrData = patient.historicalData.heartRate.map(hr => ({
      date: new Date(hr.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      fullDate: new Date(hr.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      value: hr.value,
      dateStr: hr.date
    }));
    heartRateData = filterDataByTimeRange(hrData, timeRange);
  } else {
    // Only generate mock data for Michael Smith
    heartRateData = healthMetrics.cardiovascularAnalysis.heartRate
      ? generateMockTrendData(healthMetrics.cardiovascularAnalysis.heartRate, getTrendDirection(healthMetrics.cardiovascularAnalysis.risk), timeRange)
      : [];
  }
  const heartRateIsMock = (!patient.historicalData?.heartRate || patient.historicalData.heartRate.length === 0) && patient.id === '1';

  // Weight trend - check historical data first
  const weightHistoricalFromParser = getHistoricalDataFromPatient('weight');
  const weightHistoricalFiltered = weightHistoricalFromParser.length > 0
    ? filterDataByTimeRange(weightHistoricalFromParser, timeRange)
    : [];
  const weightIsMock = weightHistoricalFiltered.length === 0 && patient.id === '1';

  // Format number to max 3 decimal places (usually 1-2)
  const formatValue = (value: number | undefined, decimals: number = 1): string => {
    if (value === undefined) return 'N/A';
    return value.toFixed(decimals);
  };

  // Mock data badge component
  const MockDataBadge = () => (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-300 ml-1">
      <Info className="w-2.5 h-2.5 mr-0.5" />
      Mock
    </span>
  );

  // Helper component to show insufficient data message
  const InsufficientDataMessage = ({ timeRange }: { timeRange: '6m' | '1y' | 'all' }) => (
    <div className="h-48 mt-2 flex items-center justify-center bg-brand-ivory-50 rounded-lg border border-brand-taupe-200">
      <div className="text-center px-4">
        <Info className="w-8 h-8 text-brand-taupe-400 mx-auto mb-2" />
        <p className="text-sm font-medium text-brand-dark-600">Not enough recent data to plot</p>
        <p className="text-xs text-brand-taupe-600 mt-1">for {getTimeRangeLabel(timeRange)}</p>
      </div>
    </div>
  );

  // Embedded chart component for cards
  const EmbeddedChart = ({
    data,
    lines,
    yAxisLabel,
    showTargets = false,
    targets = {}
  }: {
    data: any[],
    lines: Array<{ dataKey: string, stroke: string, name: string }>,
    yAxisLabel: string,
    showTargets?: boolean,
    targets?: { [key: string]: number }
  }) => {
    // Check if we have enough data points to plot (minimum 2 points)
    if (!data || data.length < 2) {
      return <InsufficientDataMessage timeRange={timeRange} />;
    }

    return (
      <div className="h-48 mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e7ddd4" />
            <XAxis
              dataKey="date"
              fontSize={11}
              tick={{ fill: '#978c81' }}
              height={24}
              interval="preserveStartEnd"
            />
            <YAxis
              fontSize={11}
              tick={{ fill: '#978c81' }}
              width={40}
              label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 10, fill: '#978c81', offset: 10 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #d8cbc3',
                borderRadius: '6px',
                fontSize: '11px',
                padding: '6px',
              }}
              formatter={(value: any, name: string) => [formatValue(value, 1), name]}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) {
                  return payload[0].payload.fullDate;
                }
                return label;
              }}
            />
            {showTargets && Object.entries(targets).map(([key, value]) => (
              <ReferenceLine
                key={key}
                y={value}
                stroke="#22c55e"
                strokeDasharray="5 5"
                strokeWidth={1.5}
                label={{ value: `Target: ${value}`, fontSize: 9, fill: '#22c55e', position: 'insideTopRight' }}
              />
            ))}
            {lines.map(line => (
              <Line
                key={line.dataKey}
                type="monotone"
                dataKey={line.dataKey}
                stroke={line.stroke}
                strokeWidth={2.5}
                name={line.name}
                dot={false}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Time Range Filter Component
  const TimeRangeFilter = () => (
    <div className="flex items-center space-x-2 mb-4">
      <span className="text-sm font-medium text-brand-dark-600 mr-2">Time Range:</span>
      {(['6m', '1y', 'all'] as const).map((range) => (
        <button
          key={range}
          onClick={() => setTimeRange(range)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            timeRange === range
              ? 'bg-brand-dark-500 text-white'
              : 'bg-white text-brand-dark-600 border border-brand-taupe-300 hover:bg-brand-beige-100'
          }`}
        >
          {range === '6m' ? '6 months' : range === '1y' ? '1 year' : 'All time'}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-2">
      {/* Time Range Filter */}
      <TimeRangeFilter />

      {/* 2-Column Layout: Card + Chart pairs */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* LEFT COLUMN */}
        <div className="space-y-2">
          {/* Obesity Analysis Card with BMI Trend */}
          <div className="metric-card p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center space-x-1.5">
                <TrendingUp className="w-4 h-4 text-warning-600" aria-hidden="true" />
                <h3 className="font-semibold text-sm text-brand-dark-600">Obesity Analysis</h3>
              </div>
              <div className={getBadgeClasses(healthMetrics.obesityAnalysis.risk as 'low' | 'medium' | 'high', 'sm')}>
                {React.createElement(getRiskIcon(healthMetrics.obesityAnalysis.risk as 'low' | 'medium' | 'high'), { className: 'w-3 h-3 mr-1', 'aria-hidden': 'true' })}
                <span className="capitalize text-xs">{healthMetrics.obesityAnalysis.risk}</span>
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-base font-bold text-brand-dark-600">
                BMI: {formatValue(healthMetrics.obesityAnalysis.bmi, 1)} kg/m²
              </div>
              <div className="text-xs text-brand-dark-400 space-y-0.5">
                {healthMetrics.obesityAnalysis.vo2Max && (
                  <div className="flex items-center justify-between">
                    <span>VO₂ max: {formatValue(healthMetrics.obesityAnalysis.vo2Max, 1)} mL/kg/min</span>
                    {!hasLabData(['VO2', 'VO₂']) && <MockDataBadge />}
                  </div>
                )}
                {healthMetrics.obesityAnalysis.bodyFatPercentage && (
                  <div className="flex items-center justify-between">
                    <span>Body Fat: {formatValue(healthMetrics.obesityAnalysis.bodyFatPercentage, 1)}%</span>
                    {!hasLabData(['Body Fat', 'Fat Percentage']) && <MockDataBadge />}
                  </div>
                )}
                {healthMetrics.obesityAnalysis.muscleMassIndex && (
                  <div className="flex items-center justify-between">
                    <span>Muscle Mass: {formatValue(healthMetrics.obesityAnalysis.muscleMassIndex, 1)} kg</span>
                    {!hasLabData(['Muscle Mass']) && <MockDataBadge />}
                  </div>
                )}
              </div>

              <div className="pt-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-brand-dark-600">BMI Trend ({getTimeRangeLabel(timeRange)})</span>
                  {bmiIsMock && <MockDataBadge />}
                </div>
                <EmbeddedChart
                  data={bmiTrend}
                  lines={[{ dataKey: 'value', stroke: '#b7a699', name: 'BMI' }]}
                  yAxisLabel="kg/m²"
                />
              </div>

              <div className="text-xs text-brand-taupe-600 pt-0.5">
                {getReferenceRangeDisplay('bmi')}
              </div>

              {/* Weight Trend */}
              {weightHistoricalFiltered.length >= 2 && (
                <div className="pt-1 mt-2 border-t border-brand-taupe-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-brand-dark-600">Weight Trend ({getTimeRangeLabel(timeRange)})</span>
                    {weightIsMock && <MockDataBadge />}
                  </div>
                  <EmbeddedChart
                    data={weightHistoricalFiltered}
                    lines={[{ dataKey: 'value', stroke: '#10b981', name: 'Weight' }]}
                    yAxisLabel="kg"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Metabolic Analysis Card with Glucose Trend */}
          <div className="metric-card p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center space-x-1.5">
                <Activity className="w-4 h-4 text-brand-dark-500" aria-hidden="true" />
                <h3 className="font-semibold text-sm text-brand-dark-600">Metabolic Analysis</h3>
              </div>
              <div className={getBadgeClasses(healthMetrics.metabolicAnalysis.risk as 'low' | 'medium' | 'high', 'sm')}>
                {React.createElement(getRiskIcon(healthMetrics.metabolicAnalysis.risk as 'low' | 'medium' | 'high'), { className: 'w-3 h-3 mr-1', 'aria-hidden': 'true' })}
                <span className="capitalize text-xs">{healthMetrics.metabolicAnalysis.risk}</span>
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-xs text-brand-dark-400 space-y-0.5">
                {healthMetrics.metabolicAnalysis.insulinLevel && (
                  <div className="flex items-center justify-between">
                    <span>Insulin: {formatValue(healthMetrics.metabolicAnalysis.insulinLevel, 1)} μU/mL</span>
                    {!hasLabData(['Fasting Insulin', 'Insulin']) && <MockDataBadge />}
                  </div>
                )}
                {healthMetrics.metabolicAnalysis.a1c && (
                  <div>HbA1c: {formatValue(healthMetrics.metabolicAnalysis.a1c, 1)}%</div>
                )}
                {healthMetrics.metabolicAnalysis.glucose && (
                  <div className="font-bold text-brand-dark-600">Glucose: {formatValue(healthMetrics.metabolicAnalysis.glucose, 1)} mg/dL</div>
                )}
                {healthMetrics.metabolicAnalysis.creatinine && (
                  <div>Creatinine: {formatValue(healthMetrics.metabolicAnalysis.creatinine, 2)} mg/dL</div>
                )}
                {healthMetrics.metabolicAnalysis.egfr && (
                  <div>eGFR: {formatValue(healthMetrics.metabolicAnalysis.egfr, 0)} mL/min</div>
                )}
              </div>

              <div className="pt-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-brand-dark-600">Glucose Trend ({getTimeRangeLabel(timeRange)})</span>
                  {glucoseIsMock && <MockDataBadge />}
                </div>
                <EmbeddedChart
                  data={glucoseTrend}
                  lines={[{ dataKey: 'value', stroke: '#f59e0b', name: 'Glucose' }]}
                  yAxisLabel="mg/dL"
                  showTargets={true}
                  targets={{ glucose: getTargetValue('glucose') || 90 }}
                />
              </div>

              <div className="text-xs text-brand-taupe-600 pt-0.5">
                Glucose: {getReferenceRangeDisplay('glucose')}, A1c: {getReferenceRangeDisplay('hba1c')}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-2">
          {/* Cholesterol Analysis Card with LDL/ApoB Trends */}
          <div className="metric-card p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center space-x-1.5">
                <Heart className="w-4 h-4 text-danger-600" aria-hidden="true" />
                <h3 className="font-semibold text-sm text-brand-dark-600">Cholesterol Analysis</h3>
              </div>
              <div className={getBadgeClasses(healthMetrics.cholesterolAnalysis.risk as 'low' | 'medium' | 'high', 'sm')}>
                {React.createElement(getRiskIcon(healthMetrics.cholesterolAnalysis.risk as 'low' | 'medium' | 'high'), { className: 'w-3 h-3 mr-1', 'aria-hidden': 'true' })}
                <span className="capitalize text-xs">{healthMetrics.cholesterolAnalysis.risk}</span>
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-base font-bold text-brand-dark-600">
                LDL: {formatValue(healthMetrics.cholesterolAnalysis.ldl, 0)} mg/dL
              </div>
              <div className="text-xs text-brand-dark-400 space-y-0.5">
                {healthMetrics.cholesterolAnalysis.apoB && (
                  <div className="flex items-center justify-between">
                    <span>ApoB: {formatValue(healthMetrics.cholesterolAnalysis.apoB, 0)} mg/dL</span>
                    {!hasLabData(['Apolipoprotein B', 'ApoB']) && <MockDataBadge />}
                  </div>
                )}
                <div>HDL: {formatValue(healthMetrics.cholesterolAnalysis.hdl, 0)} mg/dL</div>
                <div className="text-brand-taupe-500">Total: {formatValue(healthMetrics.cholesterolAnalysis.total, 0)} mg/dL</div>
              </div>

              <div className="pt-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-brand-dark-600">Cholesterol Trends ({getTimeRangeLabel(timeRange)})</span>
                  {(ldlIsMock || apoBIsMock) && <MockDataBadge />}
                </div>
                <EmbeddedChart
                  data={cholesterolTrendData}
                  lines={[
                    { dataKey: 'ldl', stroke: '#6a6055', name: 'LDL' },
                    ...(healthMetrics.cholesterolAnalysis.apoB ? [{ dataKey: 'apoB', stroke: '#ef4444', name: 'ApoB' }] : [])
                  ]}
                  yAxisLabel="mg/dL"
                  showTargets={true}
                  targets={{
                    ldl: getTargetValue('ldlCholesterol') || 100,
                    apoB: getTargetValue('apoB') || 90
                  }}
                />
              </div>

              <div className="text-xs text-brand-taupe-600 pt-0.5">
                LDL: {getReferenceRangeDisplay('ldlCholesterol')}, ApoB: {getReferenceRangeDisplay('apoB')}, HDL: {getReferenceRangeDisplay('hdlCholesterol')}
              </div>
            </div>
          </div>

          {/* Cardiovascular Analysis Card with BP Trends */}
          <div className="metric-card p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center space-x-1.5">
                <Heart className="w-4 h-4 text-danger-600" aria-hidden="true" />
                <h3 className="font-semibold text-sm text-brand-dark-600">Cardiovascular Analysis</h3>
              </div>
              <div className={getBadgeClasses(healthMetrics.cardiovascularAnalysis.risk as 'low' | 'medium' | 'high', 'sm')}>
                {React.createElement(getRiskIcon(healthMetrics.cardiovascularAnalysis.risk as 'low' | 'medium' | 'high'), { className: 'w-3 h-3 mr-1', 'aria-hidden': 'true' })}
                <span className="capitalize text-xs">{healthMetrics.cardiovascularAnalysis.risk}</span>
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-lg font-bold text-brand-dark-600">
                {formatValue(healthMetrics.cardiovascularAnalysis.systolic, 0)}/{formatValue(healthMetrics.cardiovascularAnalysis.diastolic, 0)} mmHg
              </div>
              <div className="text-xs text-brand-dark-400 space-y-0.5">
                {healthMetrics.cardiovascularAnalysis.heartRate && (
                  <div className="flex items-center justify-between">
                    <span>Heart Rate: {formatValue(healthMetrics.cardiovascularAnalysis.heartRate, 0)} bpm</span>
                    {!hasLabData(['Heart Rate']) && <MockDataBadge />}
                  </div>
                )}
                {healthMetrics.cardiovascularAnalysis.respiratoryRate && (
                  <div className="flex items-center justify-between">
                    <span>Respiratory Rate: {formatValue(healthMetrics.cardiovascularAnalysis.respiratoryRate, 0)} breaths/min</span>
                    {!hasLabData(['Respiratory Rate']) && <MockDataBadge />}
                  </div>
                )}
              </div>

              <div className="pt-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-brand-dark-600">Blood Pressure Trends ({getTimeRangeLabel(timeRange)})</span>
                  {patient.id === '1' && bloodPressureData.length > 0 && !patient.historicalData?.bloodPressure?.length && <MockDataBadge />}
                </div>
                <EmbeddedChart
                  data={bloodPressureData}
                  lines={[
                    { dataKey: 'systolic', stroke: '#ef4444', name: 'Systolic' },
                    { dataKey: 'diastolic', stroke: '#3b82f6', name: 'Diastolic' }
                  ]}
                  yAxisLabel="mmHg"
                  showTargets={true}
                  targets={{
                    systolic: getTargetValue('systolicBP') || 120,
                    diastolic: getTargetValue('diastolicBP') || 80
                  }}
                />
              </div>

              <div className="text-xs text-brand-taupe-600 pt-0.5">
                Systolic: {getReferenceRangeDisplay('systolicBP')}, Diastolic: {getReferenceRangeDisplay('diastolicBP')}
              </div>

              {/* Heart Rate Trend */}
              {healthMetrics.cardiovascularAnalysis.heartRate && heartRateData.length >= 2 && (
                <div className="pt-1 mt-2 border-t border-brand-taupe-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-brand-dark-600">Heart Rate Trend ({getTimeRangeLabel(timeRange)})</span>
                    {heartRateIsMock && <MockDataBadge />}
                  </div>
                  <EmbeddedChart
                    data={heartRateData}
                    lines={[
                      { dataKey: 'value', stroke: '#8b5cf6', name: 'Heart Rate' }
                    ]}
                    yAxisLabel="bpm"
                    showTargets={false}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
